import { useCallback } from 'react';
import { componentList, sevColors, type Issue } from '@piaip/shared';
import { sx, sxc } from '../sx';
import { useApp } from '../state';
import { api } from '../api';
import { BTN_PRIMARY, Empty, Field, INPUT, PageHead, SELECT } from '../components/ui';
import { useRowEditor } from '../components/useRowEditor';

const COMPONENT_OPTIONS = [
  { value: '', label: '— select —' },
  ...componentList().map((c) => ({ value: c.n, label: c.n })),
  { value: 'Whole scheme', label: 'Whole scheme' },
];

export function Issues() {
  const { snapshot, can, mutate } = useApp();
  const canEdit = can('issues');

  return (
    <div
      style={sx(
        'max-width:1180px;margin:0 auto;padding:20px 16px 60px;display:flex;flex-direction:column;gap:16px',
      )}
    >
      <PageHead
        title="Issues & bottlenecks register"
        note="Anything holding up work — land disputes, utility connections, contractor mobilisation, approvals."
        action={
          canEdit ? (
            <button
              type="button"
              onClick={() => void mutate(() => api.createIssue())}
              style={sx(BTN_PRIMARY)}
            >
              + Add issue
            </button>
          ) : undefined
        }
      />
      <div style={sx('display:flex;flex-direction:column;gap:14px')}>
        {(snapshot.issues ?? []).map((issue) => (
          <IssueCard key={issue.id} issue={issue} disabled={!canEdit} />
        ))}
        {(snapshot.issues ?? []).length === 0 ? (
          <Empty pad="36px">
            {canEdit
              ? 'No issue has been recorded. Add one so it appears in the delay report.'
              : 'No issue has been recorded.'}
          </Empty>
        ) : null}
      </div>
    </div>
  );
}

function IssueCard({ issue, disabled }: { issue: Issue; disabled: boolean }) {
  const { mutate } = useApp();
  const save = useCallback(
    async (patch: Partial<Issue>) => {
      await mutate(() => api.patchIssue(issue.id, patch));
    },
    [issue.id, mutate],
  );
  const { row, set, commit, commitNow } = useRowEditor(issue, save);

  const sev = sevColors(row.severity);

  return (
    <div
      style={sxc(
        'background:#fff;border:1px solid #dbe3e0;border-radius:8px;padding:16px;display:grid;grid-template-columns:repeat(auto-fit,minmax(210px,1fr));gap:12px',
        `border-left:4px solid ${sev.fg}`,
      )}
    >
      <Field label="Issue" htmlFor={`i-title-${issue.id}`}>
        <input
          id={`i-title-${issue.id}`}
          type="text"
          value={row.title}
          disabled={disabled}
          onChange={(e) => set({ title: e.target.value })}
          onBlur={() => void commit()}
          style={sx(INPUT)}
        />
      </Field>

      <Field label="Affected work" htmlFor={`i-comp-${issue.id}`}>
        <select
          id={`i-comp-${issue.id}`}
          value={row.component}
          disabled={disabled}
          onChange={(e) => void commitNow({ component: e.target.value })}
          style={sx(SELECT)}
        >
          {COMPONENT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Responsible" htmlFor={`i-owner-${issue.id}`}>
        <input
          id={`i-owner-${issue.id}`}
          type="text"
          value={row.owner}
          disabled={disabled}
          onChange={(e) => set({ owner: e.target.value })}
          onBlur={() => void commit()}
          style={sx(INPUT)}
        />
      </Field>

      <Field
        label="Effect on the scheme, in plain words"
        htmlFor={`i-impact-${issue.id}`}
        style="display:flex;flex-direction:column;gap:5px;grid-column:span 2"
      >
        <input
          id={`i-impact-${issue.id}`}
          type="text"
          value={row.impact}
          disabled={disabled}
          onChange={(e) => set({ impact: e.target.value })}
          onBlur={() => void commit()}
          placeholder="e.g. Work on 3 km of Manthar Main Drain cannot start until land is handed over"
          style={sx(INPUT)}
        />
      </Field>

      <div style={sx('display:flex;gap:10px')}>
        <Field label="Severity" htmlFor={`i-sev-${issue.id}`} style="display:flex;flex-direction:column;gap:5px;flex:1">
          <select
            id={`i-sev-${issue.id}`}
            value={row.severity}
            disabled={disabled}
            onChange={(e) => void commitNow({ severity: e.target.value as Issue['severity'] })}
            style={sx(SELECT)}
          >
            <option value="Critical">Critical</option>
            <option value="Major">Major</option>
            <option value="Minor">Minor</option>
          </select>
        </Field>
        <Field label="Status" htmlFor={`i-status-${issue.id}`} style="display:flex;flex-direction:column;gap:5px;flex:1">
          <select
            id={`i-status-${issue.id}`}
            value={row.status}
            disabled={disabled}
            onChange={(e) => void commitNow({ status: e.target.value as Issue['status'] })}
            style={sx(SELECT)}
          >
            <option value="Open">Open</option>
            <option value="In hand">In hand</option>
            <option value="Resolved">Resolved</option>
          </select>
        </Field>
      </div>

      <Field
        label="Action being taken"
        htmlFor={`i-action-${issue.id}`}
        style="display:flex;flex-direction:column;gap:5px;grid-column:span 2"
      >
        <input
          id={`i-action-${issue.id}`}
          type="text"
          value={row.action}
          disabled={disabled}
          onChange={(e) => set({ action: e.target.value })}
          onBlur={() => void commit()}
          style={sx(INPUT)}
        />
      </Field>

      <div style={sx('display:flex;gap:10px;align-items:flex-end')}>
        <Field label="Target date" htmlFor={`i-target-${issue.id}`} style="display:flex;flex-direction:column;gap:5px;flex:1">
          <input
            id={`i-target-${issue.id}`}
            type="date"
            value={row.target}
            disabled={disabled}
            onChange={(e) => void commitNow({ target: e.target.value })}
            style={sx(
              'padding:9px 10px;border:1px solid #d3dcda;border-radius:4px;font-size:13.5px;width:100%;box-sizing:border-box',
            )}
          />
        </Field>
        {!disabled ? (
          <button
            type="button"
            onClick={() => {
              if (window.confirm('Remove this issue from the register?')) {
                void mutate(() => api.deleteIssue(issue.id));
              }
            }}
            style={sx(
              'background:none;border:1px solid #e3c9c5;color:#a3372c;padding:10px 13px;border-radius:4px;font-size:13px;cursor:pointer',
            )}
          >
            Delete
          </button>
        ) : null}
      </div>
    </div>
  );
}
